import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { isMobileDevice } from 'axis-components/components/utilities';
@Component({
  host: {
    class: 'axis-dob',
    role: 'group'
  },
  selector: 'axis-dob',
  templateUrl: './dob.component.html',

  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DOBComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DOBComponent),
      multi: true
    }
  ]
})
export class DOBComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() ariaLabel = {
    day: 'Please select a day',
    month: 'Please select a month',
    year: 'Please select a year'
  };
  @Input() startYear = 1900;
  @Input() endYear: number;
  @Input() rel: string;
  @Input() required = false;
  @Input() monthFormat = 'mmm-mm';
  @Input() yearsOrderDesc = false;
  @Input() alignment = 'mm-dd-yyyy';
  @Input() dayLabel = 'Day';
  @Input() monthLabel = 'Month';
  @Input() yearLabel = 'Year';
  @Input() monthProvider;

  @Output() onSelect: EventEmitter<any> = new EventEmitter<any>();
  @Output() onDropDownClose: EventEmitter<any> = new EventEmitter<any>();

  private isLeapYear = false;
  public disabled = false;
  public model: Date;

  public days: Array<any> = new Array<any>();
  public months: Array<any> = new Array<any>();
  public years: Array<any> = new Array<any>();
  public isReadOnly = false;
  public month: any;
  public day: any;
  public year: any;

  private isMonthTouched = false;
  private isDayTouched = false;
  private isYearTouched = false;

  public isYearListLeapYear: boolean;

  @ViewChildren(forwardRef(() => DropdownComponent))
  dropdownComponents: QueryList<DropdownComponent>;

  private propagateModelChange = (_: any) => { };
  private OnTouchedEvent = () => { };

  constructor(private elementRef: ElementRef) { }

  ngOnInit() {
    if (!this.ariaLabel.day) {
      this.ariaLabel.day = 'Please select a day';
    }
    if (!this.ariaLabel.month) {
      this.ariaLabel.month = 'Please select a month';
    }
    if (!this.ariaLabel.year) {
      this.ariaLabel.year = 'Please select a year';
    }

    let daysObj: Object;
    this.isReadOnly = isMobileDevice();
    this.setYearList(false);
    this.setMonthLabel(this.monthFormat);

    for (let i = 1; i <= 30; i++) {
      daysObj = { day: ('0' + i.toString()).slice(-2) };
      this.days.push(daysObj);
    }
  }

  /**
   * set months list and update the label in months array with user defined label.
   * @param value: string
   */
  setMonthLabel(value) {
    if (this.monthProvider) {
      this.months = this.monthProvider;
    } else {
      this.months = [
        {
          month: '1',
          label: '01 (Jan)'
        },
        {
          month: '2',
          label: '02 (Feb)'
        },
        {
          month: '3',
          label: '03 (Mar)'
        },
        {
          month: '4',
          label: '04 (Apr)'
        },
        {
          month: '5',
          label: '05 (May)'
        },
        {
          month: '6',
          label: '06 (Jun)'
        },
        {
          month: '7',
          label: '07 (Jul)'
        },
        {
          month: '8',
          label: '08 (Aug)'
        },
        {
          month: '9',
          label: '09 (Sep)'
        },
        {
          month: '10',
          label: '10 (Oct)'
        },
        {
          month: '11',
          label: '11 (Nov)'
        },
        {
          month: '12',
          label: '12 (Dec)'
        }
      ];
    }
    // update the label in months array with user defined label.
    this.months.forEach(element => {
      if (value === 'mmm') {
        element.label = element.label.substr(4, 3);
      } else if (value === 'MMM') {
        element.label = element.label.substr(4, 3).toUpperCase();
      } else if (value === 'mm') {
        element.label = element.label.substr(0, 2);
      }
    });
  }

  ngAfterViewInit() {
    if (this.dropdownComponents) {
      this.dropdownComponents
        .toArray()
        .forEach((comp: DropdownComponent, index) => {
          comp.onSelect.subscribe(() => { });
        });
      this.dropdownComponents
        .toArray()
        .forEach((comp: DropdownComponent, index) => {
          comp.onTouchedEmitter.subscribe(() => { });
        });
    }
  }

  writeValue(value: any) {
    if (value || value === 0) {
      this.preselectValue(value);
    } else {
      this.model = null;
    }
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  registerOnChange(fn: any): void {
    this.propagateModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.OnTouchedEvent = fn;
  }

  validate(control: AbstractControl): { [key: string]: any } {
    if (this.required) {
      if (this.model) {
        return null;
      } else {
        const isDayValid = this.day && this.day.day;
        const isMonthValid = this.month && this.month.month;
        const isYearValid = this.year && this.year.year;
        return {
          axisdob: {
            valid: false,
            dayValid: isDayValid,
            monthValid: isMonthValid,
            yearValid: isYearValid,
            errorMessage: this.getErrorMessages(isDayValid, isMonthValid, isYearValid)
          }
        };
      }
    }
  }

  /**
   * set year's in list.
   * if leapYear, then set the list with only leap years, else set all years in list.
   * @param leapYear: boolean
   */
  setYearList(leapYear) {
    const currentYear: number = this.endYear || new Date().getFullYear();
    let yearsObj: Object;
    this.years = [];
    let isLeapYear: boolean;
    if (leapYear) {
      for (let i = this.startYear; i <= currentYear; i++) {
        isLeapYear = this.checkLeapYear(i);
        if (isLeapYear) {
          yearsObj = { year: i.toString() };
          this.years.push(yearsObj);
          this.isYearListLeapYear = true;
        }
      }
    } else {
      for (let i = this.startYear; i <= currentYear; i++) {
        yearsObj = { year: i.toString() };
        this.years.push(yearsObj);
      }
      this.isYearListLeapYear = false;
    }
    if (this.year) {
      const findYearIndex = this.years.findIndex(x => x.year === this.year['year']);
      this.year = findYearIndex > -1 ? this.years[findYearIndex] : null;
    }
    if (this.yearsOrderDesc) {
      this.years.reverse();
    }
  }

  /**
   * Check whether year is leap year or not.
   * @param year: any
   * @return isLeapYear: boolean
   */
  checkLeapYear(year: any) {
    let isLeapYear: boolean;

    if (year % 4 === 0 && year % 100 !== 0) {
      isLeapYear = true;
    } else if (year % 400 === 0) {
      isLeapYear = true;
    } else {
      isLeapYear = false;
    }
    return isLeapYear;
  }

  /**
   * generate error message
   * @param dayValid: boolean
   * @param monthValid: boolean
   * @param yearValid: boolean
   * @return errMessage: string
   */
  getErrorMessages(dayValid, monthValid, yearValid) {
    const msgDay = dayValid ? null : 'Day';
    const msgMonth = monthValid ? null : 'Month';
    const msgYear = yearValid ? null : 'Year';
    let errMessage = '';
    if (msgDay || msgMonth || msgYear) {
      errMessage = 'Please select a valid ' + [msgDay, msgMonth, msgYear].filter(function (val) {
        return val;
      }).join(' , ');
      const pos = errMessage.lastIndexOf(',');
      errMessage = errMessage.slice(0, pos) + errMessage.slice(pos).replace(',', ' and ');
    }

    return errMessage;
  }

  preselectValue(value) {
    let selectedMonth: any;
    this.year = { year: value.getFullYear().toString() };
    this.months.forEach(function (month, index) {
      if (parseInt(month.month, 10) === value.getMonth() + 1) {
        selectedMonth = month;
      }
    });
    this.month = selectedMonth;
    this.day = { day: ('0' + value.getDate().toString()).slice(-2) };
    this.model = value;
  }

  selectYear(data) {
    if (this.year) {
      this.isLeapYear = this.checkLeapYear(this.year.year);
    }
    this.selectDate();
    this.addRemoveClasses();
  }

  selectMonth(data) {
    this.calculateDays(data);
    this.selectDate();
    this.addRemoveClasses();
  }

  selectDay(data) {
    if (this.month && this.month.month === '2') {
      if (this.day && this.day.day === '29') {
        this.setYearList(true);
      } else if (this.isYearListLeapYear) {
        this.setYearList(false);
      }
    } else {
      if (this.isYearListLeapYear) {
        this.setYearList(false);
      }
    }
    this.selectDate();
    this.addRemoveClasses();
  }

  setDays(days) {
    const daysObj: Array<any> = new Array<any>();
    for (let i = 1; i <= days; i++) {
      daysObj.push({ day: ('0' + i.toString()).slice(-2) });
    }
    this.days = Array.from(daysObj);
    if (this.day) {
      const findDayIndex = this.days.findIndex(x => x.day === this.day['day']);
      this.day = findDayIndex > -1 ? this.days[findDayIndex] : null;
      if (this.month && this.month.month === '2' && findDayIndex <= -1) {
        this.year = null;
      }
    }
  }

  calculateDays(data) {
    const months31 = ['1', '3', '5', '7', '8', '10', '12'];
    if (data) {
      if (data.month === '2') {
        this.setDays(29);
        if (this.day && this.day.day === '29') {
          this.setYearList(true);
        }
      } else if (months31.indexOf(data.month) >= 0) {
        this.setDays(31);
        if (this.isYearListLeapYear) {
          this.setYearList(false);
        }
      } else {
        this.setDays(30);
        if (this.isYearListLeapYear) {
          this.setYearList(false);
        }
      }
    }
  }

  selectDate() {
    if (
      !this.isLeapYear &&
      this.month &&
      this.month.month === '2' &&
      this.day &&
      this.day.day === '29'
    ) {
      this.model = null;
    } else if (this.year && this.month && this.day) {
      this.model = new Date(this.year.year, this.month.month - 1, this.day.day);
    } else {
      this.model = null;
    }
    if (this.isMonthTouched || this.isDayTouched || this.isYearTouched) {
      this.propagateModelChange(this.model);
    }
    this.onSelect.emit(this.model);
  }

  onDOBMonthTouched(evt) {
    this.OnTouchedEvent();
    if (!this.isMonthTouched) {
      this.isMonthTouched = true;
    }
  }

  onDOBDayTouched(evt) {
    this.OnTouchedEvent();
    if (!this.isDayTouched) {
      this.isDayTouched = true;
    }
  }

  onDOBYearTouched(evt) {
    this.OnTouchedEvent();
    if (!this.isYearTouched) {
      this.isYearTouched = true;
    }
  }

  onDayOpenClose() {
    this.onDropDownClose.emit(true);
    this.addRemoveClasses();
  }

  onMonthOpenClose() {
    this.onDropDownClose.emit(true);
    this.addRemoveClasses();
  }

  onYearOpenClose() {
    this.onDropDownClose.emit(true);
    this.addRemoveClasses();
  }

  /**
   * Add/remove css classes dynamically to override the common error validation class
   * from parent component. So that the required and error classes applied on individual fields.
   */
  addRemoveClasses() {
    if (this.required) {
      this.removeClasses();

      const isDayValid = this.day && this.day.day;
      const isMonthValid = this.month && this.month.month;
      const isYearValid = this.year && this.year.year;

      if (!isMonthValid) {
        if (!this.isMonthTouched) {
          this.addClasses(0, false);
        }
      } else {
        if (!isDayValid || !isYearValid) {
          this.addClasses(0, true);
        }
      }

      if (!isDayValid) {
        if (!this.isDayTouched) {
          this.addClasses(1, false);
        }
      } else {
        if (!isMonthValid || !isYearValid) {
          this.addClasses(1, true);
        }
      }

      if (!isYearValid) {
        if (!this.isYearTouched) {
          this.addClasses(2, false);
        }
      } else {
        if (!isDayValid || !isMonthValid) {
          this.addClasses(2, true);
        }
      }
    }
  }

  /**
   * add classes to the DOM child element.
   * @param index: number
   * @param preserveWhite: boolean
   */
  addClasses(index, preserveWhite) {
    if (preserveWhite) {
      this.elementRef.nativeElement.children[index].classList.add('axis-preserve-white-bg');
    } else {
      this.elementRef.nativeElement.children[index].classList.add('axis-preserve-white-bg', 'axis-preserve-yellow-bg');
    }
  }

  /**
   * remove classes from the DOM child element
   */
  removeClasses() {
    for (let index = 0; index < 3; index++) {
      this.elementRef.nativeElement.children[index].classList.remove('axis-preserve-white-bg', 'axis-preserve-yellow-bg');
    }
  }

  reset() {
    this.month = null;
    this.day = null;
    this.year = null;
    this.model = null;
  }

  ngOnDestroy() {
    this.dropdownComponents.toArray().forEach((comp: DropdownComponent) => {
      comp.onSelect.unsubscribe();
      comp.onTouchedEmitter.unsubscribe();
    });
  }
}
